import React, { FC } from "react";
import {
  ArrayInput,
  AutocompleteInput,
  BooleanInput,
  Edit,
  EditProps,
  NumberInput,
  ReferenceInput,
  SaveButton,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
  Toolbar,
} from "react-admin";
import CrudBreadcrumb from "../../components/CrudBreadcrumb";
import HospitalRoomInput from "../../components/inputs/HospitalRoomInput";
import bookings from "./";
import { MultipleSurgeryDates } from "./MultipleSurgeryDates";
import useStyles from "./shared/useStyles";
import { OrderActions } from "./util";

const BookingEditToolbar: FC<any> = (props) => (
  <Toolbar {...props}>
    <SaveButton
      label="Revise"
      disabled={props.pristine}
      transform={(data) => {
        // Convert dates array to simple date strings array
        let processedDate;

        if (data.dates && Array.isArray(data.dates)) {
          // Extract date values from array of objects
          const dateValues = data.dates
            .map((item: any) => item?.date || item)
            .filter((d: any) => d !== null && d !== undefined && d !== "");
          processedDate = dateValues.length > 0 ? dateValues : undefined;
        } else if (data.date) {
          // Fallback for old format
          processedDate = Array.isArray(data.date) ? data.date : [data.date];
        }

        return {
          ...data,
          date: processedDate,
          dates: undefined, // Remove dates object
          action: OrderActions.REVISE,
        };
      }}
    />
  </Toolbar>
);

export const BookingEdit: React.FC<EditProps> = (props) => {
  const classes = useStyles();
  const isSR = process.env.REACT_APP_COMPANY_CODE === "SNR";

  return (
    <>
      <CrudBreadcrumb
        resourceName={bookings.name}
        mode="revise"
        resourceId={props.id}
      />
      <Edit
        {...props}
        title={`Booking #${props.id} Revise`}
        mutationMode="pessimistic"
      >
        <SimpleForm toolbar={<BookingEditToolbar />}>
          <h2>Details</h2>

          <MultipleSurgeryDates source="dates" isCreate={false} />

          <ReferenceInput
            source="revision_category_id"
            reference="revision-categories"
            label="Revision Category"
          >
            <AutocompleteInput optionText="name" />
          </ReferenceInput>

          <ReferenceInput
            source="consultant_id"
            reference="consultants"
            sort={{ field: "full_name", order: "asc" }}
          >
            <AutocompleteInput optionText="full_name" />
          </ReferenceInput>
          <BooleanInput
            source="is_eot"
            label={
              process.env.REACT_APP_COMPANY_CODE === "STMY"
                ? "Is UD?"
                : "Is EOT?"
            }
          />
          <ReferenceInput
            source="surgeon_id"
            reference="surgeons"
            sort={{ field: "full_name", order: "asc" }}
          >
            <AutocompleteInput optionText="full_name" />
          </ReferenceInput>
          <ReferenceInput
            source="hospital_id"
            reference="hospitals"
            sort={{ field: "name", order: "asc" }}
          >
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
          <HospitalRoomInput source="hospital_room_id" />
          <ReferenceInput
            source="procedure_id"
            reference="procedures"
            label={
              process.env.REACT_APP_COMPANY_CODE === "STMY"
                ? "Order Type"
                : "Procedure"
            }
          >
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
          <TextInput source="remarks" />

          <h2>{isSR ? "Devices & Consumables" : "Instruments & Implants"}</h2>
          <ArrayInput
            source="items"
            label={isSR ? "Devices & Consumables" : "Instrument & Implants"}
          >
            <SimpleFormIterator className={classes.instrument}>
              <ReferenceInput
                label="Name"
                source="product_id"
                reference="products"
                sort={{ field: "name", order: "ASC" }}
                filter={{ type: ["instrument", "implant"] }}
              >
                <AutocompleteInput optionText="name" />
              </ReferenceInput>
              <NumberInput source="quantity" label="Quantity" min="1" />
            </SimpleFormIterator>
          </ArrayInput>

          <h2>Additional Items</h2>
          <TextInput
            multiline
            source="additional_items"
            placeholder={
              isSR
                ? "Enter the additional items that are not in the Device and Consumable lists"
                : "Enter the additional items that are not in the Instrument and Implant lists"
            }
          />
        </SimpleForm>
      </Edit>
    </>
  );
};
